/**
 * SoilGroupFieldPage — in-field capture for one soil application group
 * (issue #417 Slice 4, Channel B).
 *
 * The operator confirms THAT the application happened, plus when it started
 * and finished and the EC reading. There is deliberately NO quantity input:
 * applied amounts are the programme's planned kg, frozen per orchard at plan
 * time and written server-side (spec §2.2 / §4.3).
 *
 * Local-first: every mutation generates ONE idempotency key. The online POST
 * and the offline queue item carry the same key, so a replay after reconnect
 * can never double-write.
 *
 * Reads its subject from the Dexie worklist cache so the page works with no
 * network at all.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db';
import type { CachedSoilMyGroup } from '../../db';
import { soilGroupService } from '../../services/soilGroupService';
import {
  enqueueSoilGroupStart, enqueueSoilGroupComplete, syncPendingSoilGroups, isTerminalRejection,
} from '../../services/soilGroupSyncService';
import { refreshMySoilGroups } from '../../services/soilGroupCacheService';
import { generateUUID } from '../../utils';
import PageHeader from '../shell/PageHeader';
import { Notice } from '../patterns';
import { formatMeasure } from '../../utils/formatMeasure';
import { localNowValue } from './dateTimeLocal';
import { elapsedMinutes } from './soilOperator';
import { startError, endError } from './nutritionSessionTimes';
import './nutrition.css';

type Feedback = '' | 'queued' | 'failed' | 'futureTime';

// `datetime-local` values are LOCAL wall-clock with no zone info. `new Date(value)`
// parses them as local and `.toISOString()` converts correctly to UTC for the API —
// do NOT append 'Z' (that would mis-tag the value as already-UTC).
function localToIso(value: string): string {
  return new Date(value).toISOString();
}

const SoilGroupFieldPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId, groupId } = useParams<{ sessionId: string; groupId: string }>();
  const [ec, setEc] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>('');
  // Defaults are computed ONCE at mount (the operator's "now" when they opened
  // the page); the input `max` below is recomputed fresh on every render so a
  // page left open doesn't block a legitimately current time.
  const [startAt, setStartAt] = useState(() => localNowValue());
  const [completeAt, setCompleteAt] = useState(() => localNowValue());

  const row = useLiveQuery(
    () => db.soilMyGroups.get(`${sessionId}:${groupId}`),
    [sessionId, groupId],
  ) as CachedSoilMyGroup | undefined;

  // ── HOOKS: all of them must stay ABOVE the `if (!row)` early return ───────
  // The invariant is the guard, not this spot: a hook placed BELOW `if (!row)`
  // would run on some renders and not others, changing hook order between
  // renders, and React throws. Anywhere above that guard is fine — this block,
  // the useState/useLiveQuery calls above it, or the useCallback below it.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    // 60s, not 1s: this drives a whole-minute display, and a per-second tick is
    // a re-render per second on a battery-constrained field device.
    const h = setInterval(() => setNowTick(new Date()), 60000);
    return () => clearInterval(h);
  }, []);

  // `undefined` from useLiveQuery means "Dexie has not answered yet"; a resolved
  // miss is also undefined, so a flag is needed to tell them apart. This is what
  // makes a deep link (bookmark, browser restart, a URL handed between
  // operators) into a cold cache resolve instead of flatly reporting absence.
  const [resolveTried, setResolveTried] = useState(false);
  useEffect(() => {
    if (row) return;          // already cached — nothing to resolve
    if (resolveTried) return; // one attempt only; never a retry loop
    let cancelled = false;
    refreshMySoilGroups()
      .catch(() => {})
      .then(() => { if (!cancelled) setResolveTried(true); });
    return () => { cancelled = true; };
  }, [row, resolveTried]);

  const afterWrite = useCallback(async () => {
    await refreshMySoilGroups().catch(() => {});
  }, []);

  const onStart = async () => {
    if (!sessionId || !groupId || !row) return;
    // Recompute against a fresh 'now' at submit time — `max` alone is not
    // enforcement (it can be typed past, and browsers vary).
    if (startError({ startedAt: startAt, now: new Date() })) {
      setFeedback('futureTime');
      return;
    }
    setBusy(true); setFeedback('');
    const payload = { startedAt: localToIso(startAt), startKey: generateUUID() };
    try {
      await soilGroupService.startGroup(sessionId, groupId, payload);
      await afterWrite();
    } catch (err) {
      if (isTerminalRejection(err)) {
        // A terminal 4xx (e.g. 400 INVALID_TRANSITION) can never succeed by
        // retrying — queueing it would strand it in the offline queue forever
        // with no signal. Tell the operator now instead.
        setFeedback('failed');
      } else {
        // Same key on the queued item — a later replay is idempotent, not a second write.
        await enqueueSoilGroupStart({ sessionId, groupId, label: row.label, payload });
        setFeedback('queued');
        syncPendingSoilGroups().catch(() => {});
      }
    } finally {
      setBusy(false);
    }
  };

  const onComplete = async () => {
    if (!sessionId || !groupId || !row) return;
    // Recompute against a fresh 'now' at submit time — same rationale as onStart.
    // Only the future check gates submission here; end-before-start remains the
    // backend's call (it has the frozen `startedAt` of record).
    if (endError({ startedAt: row.startedAt ?? undefined, endedAt: completeAt, now: new Date() }) === 'endInFuture') {
      setFeedback('futureTime');
      return;
    }
    setBusy(true); setFeedback('');
    const parsedEc = ec.trim() === '' ? undefined : Number(ec);
    const payload = {
      completedAt: localToIso(completeAt),
      ...(parsedEc !== undefined && Number.isFinite(parsedEc) ? { ecReading: parsedEc } : {}),
      captureChannel: 'field' as const,
      completeKey: generateUUID(),
    };
    try {
      await soilGroupService.completeGroup(sessionId, groupId, payload);
      await afterWrite();
      navigate('/nutrition/soil-groups');
    } catch (err) {
      if (isTerminalRejection(err)) {
        // e.g. 422 UNIT_CONFLICT or 400 SNAPSHOT_MISMATCH/BAD_TIMES — the
        // submission can never drain, so do not enqueue it.
        setFeedback('failed');
      } else {
        await enqueueSoilGroupComplete({ sessionId, groupId, label: row.label, payload });
        setFeedback('queued');
        syncPendingSoilGroups().catch(() => {});
      }
    } finally {
      setBusy(false);
    }
  };

  // Glove-operable EC adjustment. `Math.round((current + delta) * 10) / 10` is
  // load-bearing: 0.2 + 0.1 is 0.30000000000000004 in binary floating point, so
  // the THIRD tap on "+" would put that on screen without the re-rounding.
  const stepEc = (delta: number) => {
    const current = ec.trim() === '' ? 0 : Number(ec);
    const next = Number.isFinite(current) ? Math.max(0, Math.round((current + delta) * 10) / 10) : 0;
    setEc(String(next));
  };

  if (!row) {
    if (!resolveTried) {
      return (
        <div className="pat-page">
          <PageHeader title={t('nutrition.soil.field.title')} />
          <Notice tone="info" testId="soil-field-resolving">{t('nutrition.soil.field.loading')}</Notice>
        </div>
      );
    }
    return (
      <div className="pat-page">
        <PageHeader title={t('nutrition.soil.field.title')} />
        <Notice tone="warn">
          {navigator.onLine
            ? t('nutrition.soil.field.notFound')
            : t('nutrition.soil.field.notFoundOffline')}
        </Notice>
        <button type="button" className="pat-btn" onClick={() => navigate('/nutrition/soil-groups')}>
          {t('nutrition.soil.field.back')}
        </button>
      </div>
    );
  }

  // Each of the three fields is INDEPENDENTLY optional, so the line is built a
  // segment at a time and absent segments are dropped rather than zero-filled.
  //
  // This is reachable, not theoretical: Orchard.numberOfTrees and
  // Orchard.areaHectares have no default and are not required, and
  // soilSignoffService sends `treeCount: group.treeCount || 0` unconditionally.
  // A tenant that records hectares but not tree counts would otherwise read
  // "Valencia · 0 trees · 3.5 ha" — a missing count presented as a fact, and on
  // a mixed group a confident undercount.
  //
  // areaHa is a float SUM across the group's orchards (soilSessionService:
  // `reduce((s, f) => s + (f.areaHa || 0), 0)`), so 1.1 + 2.2 is
  // 3.3000000000000003 — it must go through formatMeasure like every other
  // number on this page.
  const factParts: string[] = [];
  if (row.cultivars && row.cultivars.length) factParts.push(row.cultivars.join(', '));
  // Guard on the ROUNDED value, not the raw one: Orchard.numberOfTrees has no
  // integer constraint (backend/src/models/Orchard.js) and soilSessionService
  // sums it across a group's orchards, so a group total in the open interval
  // (0, 0.5) is truthy but rounds to display "0" — the same "0 trees"
  // misrepresentation the "absent" rule above exists to prevent.
  const roundedTreeCount = row.treeCount ? Math.round(row.treeCount) : 0;
  if (roundedTreeCount) {
    factParts.push(t('nutrition.soil.field.factsTrees', { count: roundedTreeCount }));
  }
  if (row.areaHa) {
    factParts.push(t('nutrition.soil.field.factsHa', { ha: formatMeasure(row.areaHa) }));
  }
  const facts = factParts.length ? factParts.join(' · ') : null;
  const minutes = elapsedMinutes(row.startedAt, nowTick);

  return (
    <div className="pat-page" data-testid="soil-group-field-page">
      <PageHeader
        title={t('nutrition.soil.field.title')}
        meta={`${row.productName} · ${row.month} · ${row.label}`}
      />

      {feedback === 'queued' && <Notice tone="info">{t('nutrition.soil.field.queued')}</Notice>}
      {feedback === 'failed' && <Notice tone="danger">{t('nutrition.soil.field.failed')}</Notice>}
      {feedback === 'futureTime' && <Notice tone="danger">{t('nutrition.soil.field.futureTime')}</Notice>}

      <div className="pat-card">
        <div className="ntr-sg-chips">
          {row.orchardNames.map((name) => (
            <span key={name} className="ntr-sg-chip" data-testid={`soil-field-chip-${name}`}>{name}</span>
          ))}
        </div>
        {facts && <div className="ntr-sg-facts" data-testid="soil-field-facts">{facts}</div>}
        <div className="ntr-sg-hero" data-testid="soil-field-hero">
          <span className="ntr-sg-hero__value">{formatMeasure(row.plannedKg)}</span>
          <span className="ntr-sg-hero__unit">kg</span>
        </div>
        {minutes !== null && (
          <div className="ntr-sg-elapsed" data-testid="soil-field-elapsed">
            {t('nutrition.soil.field.elapsed', { count: minutes })}
          </div>
        )}
        <p className="ntr-field-note">{t('nutrition.soil.field.confirm')}</p>
      </div>

      {row.status === 'not_started' ? (
        <div className="pat-card">
          <div className="ntr-sg-panel">
            <label htmlFor="soil-field-start-at">
              {t('nutrition.soil.field.startTime')}
            </label>
            <input
              id="soil-field-start-at" data-testid="soil-field-start-at" type="datetime-local"
              className="pat-input" value={startAt} max={localNowValue()}
              onChange={(e) => setStartAt(e.target.value)}
            />
            <button
              type="button" className="pat-btn pat-btn--primary ntr-sg-action" data-testid="soil-field-start"
              disabled={busy} onClick={onStart}
            >
              {t('nutrition.soil.field.start')}
            </button>
          </div>
        </div>
      ) : (
        <div className="pat-card">
          <div className="ntr-sg-panel">
            <label htmlFor="soil-field-ec">{t('nutrition.soil.field.ec')}</label>
            <div className="ntr-stepper">
              <button
                type="button" data-testid="soil-field-ec-down"
                aria-label={t('nutrition.soil.field.ecDown')} onClick={() => stepEc(-0.1)}
              >
                −
              </button>
              <input
                id="soil-field-ec" data-testid="soil-field-ec" type="number" min="0" step="0.1"
                className="pat-input" value={ec} onChange={(e) => setEc(e.target.value)}
              />
              <button
                type="button" data-testid="soil-field-ec-up"
                aria-label={t('nutrition.soil.field.ecUp')} onClick={() => stepEc(0.1)}
              >
                +
              </button>
            </div>
            <label htmlFor="soil-field-complete-at">
              {t('nutrition.soil.field.completeTime')}
            </label>
            <input
              id="soil-field-complete-at" data-testid="soil-field-complete-at" type="datetime-local"
              className="pat-input" value={completeAt} max={localNowValue()}
              min={row.startedAt ? localNowValue(new Date(row.startedAt)) : undefined}
              onChange={(e) => setCompleteAt(e.target.value)}
            />
            {/* The system records the PLANNED kg verbatim — nothing here may imply
                a measurement it does not take (spec §2.7). */}
            <p className="ntr-sg-note" data-testid="soil-field-applied">
              {t('nutrition.soil.field.appliedAsPlanned', { kg: formatMeasure(row.plannedKg) })}
            </p>
            <button
              type="button" className="pat-btn pat-btn--primary ntr-sg-action" data-testid="soil-field-complete"
              disabled={busy} onClick={onComplete}
            >
              {t('nutrition.soil.field.complete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoilGroupFieldPage;
